import * as yup from "yup";
import { Input } from "@components/forms/input";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  ICompleteClientProfileProps,
  useAuthContext,
} from "@hooks/contexts/auth-context";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  FaAt,
  FaCheckCircle,
  FaIdCard,
  FaLock,
  FaRegUser,
  FaSave,
  FaSearch,
  FaSignInAlt,
  FaTimesCircle,
} from "react-icons/fa";
import { FaChartLine } from "react-icons/fa6";
import { Masks } from "@hooks/functions/Masks/Masks";
import dayjs from "dayjs";
import { getClientStatus } from "@hooks/functions/Client/status";
import { ModalType, useModalStore } from "@hooks/stores/zustand-modal-store";
import { formatName } from "@hooks/functions/formatName";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
}

interface IAtSignForm {
  atSign: string;
}

const masks = new Masks();

const onlyDigits = (value?: string) => String(value || "").replace(/\D/g, "");
const normalizeAtSign = (value?: string) =>
  String(value || "")
    .trim()
    .replace(/^@+/, "")
    .toLowerCase();
const atSignValidationMessage =
  "Use apenas letras, números e underline (_), sem espaços, acentos ou caracteres especiais.";

export function ModalClientManagementIdentified({ isOpen, onClose }: IProps) {
  const {
    client,
    completeClientProfile,
    checkAtSignAvailability,
    updateClientAtSign,
  } = useAuthContext();
  const openModal = useModalStore((state) => state.openModal);

  const [animate, setAnimate] = useState(false);
  const [activeTab, setActiveTab] = useState<"profile" | "atSign">("profile");
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isCheckingAtSign, setIsCheckingAtSign] = useState(false);
  const [isSavingAtSign, setIsSavingAtSign] = useState(false);
  const [atSignAvailability, setAtSignAvailability] = useState<
    "idle" | "available" | "unavailable"
  >("idle");

  const clientData = client?.client;
  const isCompleteClient = clientData?.type === "c";
  const missingName = !clientData?.name;
  const missingEmail = !isCompleteClient && !clientData?.email;
  const missingCellPhone = !isCompleteClient && !clientData?.cellPhone;
  const missingAtSign = !clientData?.atSign;
  const canCompleteProfile = Boolean(clientData && !isCompleteClient);
  const canEditAtSign = Boolean(clientData && missingAtSign);

  const profileScheme = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().required("Nome é obrigatório"),
        email: yup
          .string()
          .email("E-mail inválido")
          .required("E-mail é obrigatório"),
        cellPhone: yup
          .string()
          .required("Celular é obrigatório")
          .matches(/^\d{2} \d{5}-\d{4}$/, "Celular inválido"),
        password: yup
          .string()
          .required("Senha é obrigatória")
          .min(6, "Senha deve ter ao menos 6 caracteres"),
        passwordConfirm: yup
          .string()
          .required("Confirme a senha")
          .oneOf([yup.ref("password")], "As senhas não conferem"),
      }),
    [],
  );

  const atSignScheme = useMemo(
    () =>
      yup.object().shape({
        atSign: yup
          .string()
          .required("@usuario é obrigatório")
          .matches(/^@?[a-zA-Z0-9_]{3,50}$/, atSignValidationMessage),
      }),
    [],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ICompleteClientProfileProps>({
    resolver: yupResolver(profileScheme) as any,
    mode: "onSubmit",
    reValidateMode: "onBlur",
  });

  const {
    register: registerAtSign,
    handleSubmit: handleSubmitAtSign,
    reset: resetAtSign,
    watch: watchAtSign,
    formState: { errors: atSignErrors },
  } = useForm<IAtSignForm>({
    resolver: yupResolver(atSignScheme) as any,
    mode: "onSubmit",
    reValidateMode: "onBlur",
  });

  const watchedAtSign = watchAtSign("atSign");
  const cleanWatchedAtSign = normalizeAtSign(watchedAtSign);
  const canSaveAtSign =
    canEditAtSign &&
    cleanWatchedAtSign.length > 0 &&
    atSignAvailability === "available";

  const handleCancel = useCallback(() => {
    setAnimate(false);
    setTimeout(() => onClose(), 300);
  }, [onClose]);

  const handleEscape = useCallback(
    (evt: KeyboardEvent) => {
      if (evt.key === "Escape") handleCancel();
    },
    [handleCancel],
  );

  function handleOpenLogin() {
    handleCancel();
    openModal(ModalType.LOGIN, { login: true });
  }

  async function handleSaveProfile(formData: ICompleteClientProfileProps) {
    setIsSavingProfile(true);
    try {
      await completeClientProfile({
        ...formData,
        name: formatName(formData.name.trim()),
        email: formData.email.trim().toLowerCase(),
        cellPhone: onlyDigits(formData.cellPhone),
      });
    } finally {
      setIsSavingProfile(false);
    }
  }

  async function handleCheckAtSign(formData: IAtSignForm) {
    setIsCheckingAtSign(true);
    try {
      const available = await checkAtSignAvailability(formData.atSign);
      setAtSignAvailability(available ? "available" : "unavailable");
    } catch {
      //
    } finally {
      setIsCheckingAtSign(false);
    }
  }

  async function handleSaveAtSign(formData: IAtSignForm) {
    setIsSavingAtSign(true);
    try {
      await updateClientAtSign(formData.atSign);
      setAtSignAvailability("idle");
    } catch {
      //
    } finally {
      setIsSavingAtSign(false);
    }
  }

  useEffect(() => {
    if (isOpen && clientData) {
      reset({
        name: clientData.name ? formatName(clientData.name) : "",
        email: isCompleteClient ? "*****@*****.***" : clientData.email || "",
        cellPhone: isCompleteClient
          ? "(**) *****-****"
          : clientData.cellPhone
            ? masks.phone(clientData.cellPhone)
            : "",
        password: "",
        passwordConfirm: "",
      });
      resetAtSign({
        atSign: clientData.atSign || "",
      });
      setActiveTab("profile");
      setAtSignAvailability("idle");
    }
  }, [isOpen, clientData, isCompleteClient, reset, resetAtSign]);

  useEffect(() => {
    setAtSignAvailability("idle");
  }, [watchedAtSign]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }

    setAnimate(false);
  }, [isOpen, handleEscape]);

  return (
    <>
      <div
        className={`modal fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        aria-hidden={!isOpen}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                <FaChartLine />
                Minha Conta
              </h5>
              <button className="btn-close" onClick={handleCancel} />
            </div>

            <div className="px-3 pt-2">
              <ul className="nav nav-tabs">
                <li className="nav-item">
                  <button
                    className={`nav-link d-flex align-items-center gap-2 ${
                      activeTab === "profile" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("profile")}
                  >
                    <FaRegUser />
                    Cadastro
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link d-flex align-items-center gap-2 ${
                      activeTab === "atSign" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("atSign")}
                  >
                    <FaAt />
                    Usuário
                  </button>
                </li>
              </ul>
            </div>

            <div className="modal-body">
              {!canCompleteProfile && (
                <div className="alert alert-warning d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                  <span>
                    Esta conta já tem cadastro completo. Para alterar dados,
                    autentique sua sessão.
                  </span>
                  <button
                    className="btn btn-warning d-flex align-items-center justify-content-center gap-2"
                    onClick={handleOpenLogin}
                  >
                    <FaSignInAlt />
                    Autenticar
                  </button>
                </div>
              )}

              {activeTab === "profile" && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(handleSaveProfile)();
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                    <div className="d-flex align-items-center gap-2 fw-semibold">
                      <FaIdCard />
                      Dados cadastrais
                    </div>
                    {canCompleteProfile && (
                      <button
                        type="submit"
                        className="btn btn-primary d-flex align-items-center gap-2"
                        disabled={isSavingProfile}
                      >
                        <FaSave />
                        {isSavingProfile ? "Salvando..." : "Salvar cadastro"}
                      </button>
                    )}
                  </div>

                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <Input
                        animated
                        label="Nome"
                        placeholder="Seu nome"
                        type="text"
                        register={register("name")}
                        errorMessage={String(errors?.name?.message || "")}
                        disabled={!canCompleteProfile}
                        readOnly={canCompleteProfile && !missingName}
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <Input
                        animated
                        label="E-mail"
                        placeholder="nome@email.com"
                        type="text"
                        register={register("email")}
                        errorMessage={String(errors?.email?.message || "")}
                        disabled={!canCompleteProfile}
                        readOnly={canCompleteProfile && !missingEmail}
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <Input
                        animated
                        label="Celular"
                        type="text"
                        mask="phone"
                        register={register("cellPhone")}
                        errorMessage={String(errors?.cellPhone?.message || "")}
                        disabled={!canCompleteProfile}
                        readOnly={canCompleteProfile && !missingCellPhone}
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <Input
                        animated
                        label="Documento"
                        placeholder="***.***.***-**"
                        mask="cpf"
                        type="text"
                        value={
                          clientData?.document
                            ? masks.cpf(clientData.document)
                            : ""
                        }
                        disabled={true}
                      />
                    </div>
                    {canCompleteProfile && (
                      <>
                        <div className="col-12 col-md-6">
                          <Input
                            animated
                            label="Senha"
                            type="password"
                            register={register("password")}
                            errorMessage={String(
                              errors?.password?.message || "",
                            )}
                          />
                        </div>
                        <div className="col-12 col-md-6">
                          <Input
                            animated
                            label="Confirmar senha"
                            type="password"
                            register={register("passwordConfirm")}
                            errorMessage={String(
                              errors?.passwordConfirm?.message || "",
                            )}
                          />
                        </div>
                      </>
                    )}
                    <div className="col-12 col-md-6">
                      <Input
                        animated
                        label="Status"
                        type="text"
                        value={
                          clientData?.status
                            ? getClientStatus(clientData.status).text
                            : "Não identificado"
                        }
                        disabled={true}
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <Input
                        animated
                        label="Membro desde"
                        type="text"
                        value={
                          clientData?.createdAt
                            ? dayjs(clientData.createdAt).format(
                                "DD/MM/YYYY HH:mm:ss",
                              )
                            : ""
                        }
                        disabled={true}
                      />
                    </div>
                  </div>
                </form>
              )}

              {activeTab === "atSign" && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmitAtSign(handleSaveAtSign)();
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                    <div className="d-flex align-items-center gap-2 fw-semibold">
                      <FaAt />
                      Nome de usuário
                    </div>
                    {atSignAvailability === "available" && (
                      <span className="badge bg-success d-flex align-items-center gap-1">
                        <FaCheckCircle />
                        Disponível
                      </span>
                    )}
                    {atSignAvailability === "unavailable" && (
                      <span className="badge bg-danger d-flex align-items-center gap-1">
                        <FaTimesCircle />
                        Indisponível
                      </span>
                    )}
                  </div>

                  {!canEditAtSign && (
                    <div className="alert alert-light border d-flex align-items-center gap-2">
                      <FaLock />
                      <span>
                        O @usuario só pode ser definido quando ainda está em
                        branco.
                      </span>
                    </div>
                  )}

                  <div className="border rounded-2 p-3">
                    <label className="form-label fw-semibold mb-2">
                      @usuario
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">@</span>
                      <input
                        className={`form-control ${
                          atSignErrors?.atSign?.message ? "is-invalid" : ""
                        }`}
                        placeholder="usuario"
                        type="text"
                        disabled={!canEditAtSign}
                        {...registerAtSign("atSign")}
                      />
                    </div>
                    {atSignErrors?.atSign?.message && (
                      <div className="invalid-feedback d-block">
                        {String(atSignErrors.atSign.message)}
                      </div>
                    )}
                    <small className="text-muted d-block mt-2">
                      {atSignValidationMessage}
                    </small>
                    <div className="d-flex flex-column flex-sm-row justify-content-end gap-2 mt-3">
                      <button
                        type="button"
                        className="btn btn-outline-primary d-flex align-items-center justify-content-center gap-2"
                        disabled={!canEditAtSign || isCheckingAtSign}
                        onClick={handleSubmitAtSign(handleCheckAtSign)}
                      >
                        <FaSearch />
                        {isCheckingAtSign ? "Consultando..." : "Consultar"}
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary d-flex align-items-center justify-content-center gap-2"
                        disabled={!canSaveAtSign || isSavingAtSign}
                      >
                        <FaSave />
                        {isSavingAtSign ? "Salvando..." : "Salvar"}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>

            <div className="modal-footer border-0">
              <button
                className="btn btn-outline-secondary px-5"
                onClick={handleCancel}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal-backdrop fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        onClick={handleCancel}
      />
    </>
  );
}
